import { memo, useMemo } from 'react';
import styled from '@emotion/styled';
import { Rate, Tag } from 'antd';
import dayjs from 'dayjs';
import { useTranslate } from '@tolgee/react';

import { isTiKTok } from 'lib/utils/isTikTok';
import { MediaViewer } from 'components/MediaViewer';
import type { MediaItem } from 'components/MediaViewer';
import { useSlide } from './useSlide';

export const imageCount = 3;

const BetweenEnd = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 8px;
`;

const StarRate = styled(Rate)`
  font-size: 14px;
  line-height: 28px;
  > li {
    margin-right: 3px !important;
  }
`;

const RateList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  > li {
    display: flex;
    align-items: center;
    > span {
      margin-right: 5px;
    }
  }
`;

export const Operation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 40px;
`;

export const Action = styled.span<{ disabled?: boolean }>`
  width: 16px;
  height: 16px;
  line-height: 16px;
  font-size: 10px;
  text-align: center;
  color: ${({ disabled = false }) => (disabled ? '#D9DDE4' : '#A1A3A8')};
  background: var(--fett-bg-app);
  cursor: ${({ disabled = false }) => (disabled ? 'not-allowed' : 'pointer')};
  /* 暗色下 disabled 原本 #D9DDE4 比 enabled 的 #A1A3A8 更亮，对比反了；改用更暗的 quaternary */
  [data-color-mode='dark'] & {
    color: ${({ disabled = false }) => (disabled ? 'var(--fett-text-quaternary)' : '#A1A3A8')};
  }
`;

export const Text = styled.p`
  font-size: 12px;
  color: var(--fett-text-secondary);
  word-break: break-all;
  margin: 0;
`;

export interface CommentContentViewProps {
  comment: Conversation.Trade.Comment;
  platform: string;
  displayTranslation: boolean;
  translation?: string;
  className?: string;
  isFollowComment?: boolean;
}

function CommentContentView(props: CommentContentViewProps) {
  const { t } = useTranslate();
  const { comment, platform, displayTranslation, translation, className, isFollowComment } = props;

  const { imageUrl = [], videoUrl = [], buyerComment, updateTime } = comment;

  const mediaItems = useMemo<MediaItem[]>(() => {
    const videos = (videoUrl || []).map((src) => ({ type: 'video' as const, src }));
    const images = (imageUrl || []).map((src) => ({ type: 'image' as const, src }));
    // 保持与历史展示一致：视频在前，图片在后
    return [...videos, ...images];
  }, [imageUrl, videoUrl]);

  const ratingNode = useMemo(() => {
    const {
      rawRatings: { ratingStar, productRating, logisticsRating, sellerRating },
    } = comment;

    if (platform.toUpperCase() === 'LAZADA') {
      return (
        <RateList>
          <li>
            <span>{t('review.整体评分')}</span>
            <StarRate allowHalf disabled count={5} value={ratingStar} />
          </li>
          <li>
            <span>{t('review.商品质量')}</span>
            <StarRate allowHalf disabled count={5} value={productRating} />
          </li>
          <li>
            <span>{t('review.物流评分')}</span>
            <StarRate allowHalf disabled count={5} value={logisticsRating} />
          </li>
          <li>
            <span>{t('review.卖家服务评分')}</span>
            <StarRate allowHalf disabled count={5} value={sellerRating} />
          </li>
        </RateList>
      );
    }

    if (isTiKTok(platform) && isFollowComment) {
      return <Tag color="blue">{t('追评')}</Tag>;
    }

    return <StarRate allowHalf disabled value={ratingStar} />;
  }, [comment, platform, isFollowComment, t]);

  const mediaLength = mediaItems.length;
  const slide = useSlide({
    count: imageCount,
    length: mediaLength,
    initialIndex: 0,
  });

  const pagedMediaItems = useMemo<MediaItem[]>(() => {
    if (mediaItems.length === 0) return [];
    const start = slide.index % mediaItems.length;
    return [...mediaItems.slice(start), ...mediaItems.slice(0, start)];
  }, [mediaItems, slide.index]);

  return (
    <div className={className}>
      <BetweenEnd>
        {ratingNode}
        {imageCount <= mediaLength && (
          <Operation>
            <Action disabled={slide.preDisabled} onClick={slide.pre}>
              ‹
            </Action>
            <Action disabled={slide.nextDisabled} onClick={slide.next}>
              ›
            </Action>
          </Operation>
        )}
      </BetweenEnd>

      {mediaLength > 0 && (
        <MediaViewer
          items={pagedMediaItems}
          thumbSize={60}
          gap={8}
          maxThumbs={imageCount}
          photoClosable
        />
      )}

      <Text>{displayTranslation ? translation : buyerComment}</Text>
      <div className="text-[12px] text-[#aaa] mb-[6px] mt-[-6px]">
        {dayjs(Number(updateTime)).format('YYYY-MM-DD HH:mm:ss')}
      </div>
    </div>
  );
}

export default memo(CommentContentView);
